import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import { pointsLabel } from '../../common/utils/pointsLabel';

/**
 * Painel "Como ganhar pontos". Responde à pergunta que mais chega ao admin
 * pelo suporte do fórum sem exigir que ele escreva um tópico à mão: todas as
 * regras já vêm das configurações do painel administrativo via
 * `serializeToForum`, então o conteúdo acompanha o que o admin configurou.
 *
 * Regras com valor zero não aparecem — anunciar "0 pontos por post" é pior
 * que omitir. Regras de curtida dependem de `pointSystemLikesEnabled`, porque
 * as duas configurações continuam salvas mesmo em fóruns sem flarum/likes.
 */

export default class HowToEarnModal extends Modal {
  className() {
    return 'PointSystemHowToEarnModal Modal--small';
  }
  title() {
    return app.translator.trans('ramon-point-system.forum.how_to_earn.title');
  }
  content() {
    const t = (k, vars) => app.translator.trans('ramon-point-system.forum.how_to_earn.' + k, vars);
    const rules = earnRules();
    const note = adminNote();
    const icon = app.forum.attribute('pointSystem.currency_icon') || 'fas fa-coins';
    return m("div", {
      className: "Modal-body PointSystemHowToEarn"
    }, rules.length > 0 && m("p", {
      className: "PointSystemHowToEarn-intro"
    }, t('intro')), rules.length > 0 ? m("ul", {
      className: "PointSystemHowToEarn-list"
    }, rules.map(rule => m("li", {
      className: "PointSystemHowToEarn-rule"
    }, m("span", {
      className: "PointSystemHowToEarn-rule-icon"
    }, m("i", {
      className: rule.icon,
      "aria-hidden": "true"
    })), m("span", {
      className: "PointSystemHowToEarn-rule-label"
    }, t('rule_' + rule.key)), m("span", {
      className: "PointSystemHowToEarn-rule-amount"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), " +", rule.amount.toLocaleString())))) : m("p", {
      className: "PointSystemHowToEarn-empty"
    }, t(systemOff() ? 'system_off' : 'no_rules')), note && m("p", {
      className: "PointSystemHowToEarn-note"
    }, note), rules.length > 0 && m("p", {
      className: "PointSystemHowToEarn-footer"
    }, t('footer', {
      unit: pointsLabel(app)
    })), m("div", {
      className: "Form-group PointSystemHowToEarn-actions"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.hide()
    }, t('close'))));
  }
}

/**
 * Lê o valor de uma regra do payload do fórum. `intval` no backend garante
 * número, mas um fórum que nunca salvou a configuração devolve `undefined`
 * — daí o fallback explícito em vez de `Number(undefined) === NaN`.
 */
function amount(key) {
  const raw = app.forum.attribute('pointSystem.' + key);
  const n = Number(raw != null ? raw : 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Chave geral desligada: o fórum não concede ponto nenhum, nem automático
 * nem manual. Mais específico que `autoAwardsOff()` e por isso testado
 * primeiro — as duas mensagens dizem coisas diferentes ao usuário.
 */
export function systemOff() {
  var _app$forum, _app$forum$attribute;
  return ((_app$forum = app.forum) == null || (_app$forum$attribute = _app$forum.attribute) == null ? void 0 : _app$forum$attribute.call(_app$forum, 'pointSystem.enabled')) === false;
}

/** True quando o admin desligou "pontos por ação" mas manteve o sistema no ar. */
export function autoAwardsOff() {
  var _app$forum2, _app$forum2$attribute;
  return ((_app$forum2 = app.forum) == null || (_app$forum2$attribute = _app$forum2.attribute) == null ? void 0 : _app$forum2$attribute.call(_app$forum2, 'pointSystemAutoAwardsEnabled')) === false;
}

/** Regras ativas, na ordem em que fazem sentido para quem está começando. */
export function earnRules() {
  if (systemOff() || autoAwardsOff()) return [];
  const likes = app.forum.attribute('pointSystemLikesEnabled') === true;
  const candidates = [{
    key: 'discussion',
    icon: 'fas fa-comments',
    amount: amount('points_per_discussion')
  }, {
    key: 'post',
    icon: 'fas fa-reply',
    amount: amount('points_per_post')
  }, {
    key: 'daily_login',
    icon: 'fas fa-calendar-check',
    amount: amount('daily_login_bonus')
  }, {
    key: 'registration',
    icon: 'fas fa-user-plus',
    amount: amount('points_per_registration')
  }];
  if (likes) {
    candidates.push({
      key: 'like_received',
      icon: 'fas fa-heart',
      amount: amount('points_per_like_received')
    }, {
      key: 'like_given',
      icon: 'fas fa-thumbs-up',
      amount: amount('points_per_like_given')
    });
  }
  return candidates.filter(r => r.amount > 0);
}

/**
 * Nota livre do admin. Renderizada como texto puro pelo Mithril — nunca com
 * `m.trust` — então HTML colado na configuração aparece escapado.
 */
export function adminNote() {
  const raw = app.forum.attribute('pointSystem.earn_help_extra');
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Verdadeiro quando há algo a mostrar. O botão some quando o admin zerou
 * todas as regras e não escreveu nota — abrir um painel vazio é pior que
 * não oferecer o atalho. Nos dois modos desligados o botão FICA: "quem
 * distribui pontos é a equipe" e "o fórum não está concedendo pontos" são
 * exatamente as respostas que o usuário procura nesses estados.
 */
export function hasEarnHelp() {
  return systemOff() || autoAwardsOff() || earnRules().length > 0 || adminNote() !== '';
}
flarum.reg.add('ramon-point-system', 'forum/components/HowToEarnModal', HowToEarnModal);